import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DESIGN_SKILLS, SKILL_ROUTE_BASE } from './priorities-ai-design/designSkills';
import { BarRow, Card, Chip, DesignLanguageStyles, Mark, SectionHead } from './designLanguageParts';

/*
 * The design language front door — v3, the daylight editorial voice.
 * Derived from the investor deck the owner ratified (2026-08-29): warm paper
 * ground, ink Spectral display with a single gold-italic phrase, wide-tracked
 * slate caps for eyebrows, left accent bars, cream callouts, square geometry.
 * Tokens and shared parts live in designLanguageParts.tsx.
 */

/* Palette strip — the accents with their laws. */
const PALETTE: { name: string; varName: string; law: string }[] = [
  { name: 'Ink', varName: '--p-ink', law: 'Display and reading. The voice.' },
  { name: 'Slate', varName: '--p-label', law: 'Labels, structure, wayfinding.' },
  { name: 'Gold', varName: '--p-gold', law: 'One emphasized phrase, never two. In diagrams, the divergence.' },
  { name: 'Forest', varName: '--p-forest', law: 'Health, execution, proof.' },
  { name: 'Clay', varName: '--p-clay', law: 'Cost, urgency, warning.' },
];

const NAV_LINKS: [string, string][] = [['/home', '← Overview']];

/* Bars for the skill rows, in listing order. */
const SKILL_BARS = ['var(--p-gold)', 'var(--p-label)', 'var(--p-forest)'];

export const DesignLanguage: React.FC = () => {
  const [assembled, setAssembled] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setAssembled(true), 1100);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div className="dl3 -m-6 min-h-screen px-6 pb-0 pt-2 sm:-m-8 sm:px-10">
      <DesignLanguageStyles />
      <div className="mx-auto max-w-5xl">

        {/* page furniture: quiet nav row */}
        <div className="flex items-center justify-between py-5">
          {NAV_LINKS.map(([to, label]) => (
            <Link key={to} to={to} className="eyebrow text-[13px] transition-colors hover:!text-[var(--p-ink)]">
              {label}
            </Link>
          ))}
        </div>

        {/* ── Masthead — the deck cover, echoed ── */}
        <header className="flex flex-col items-center border-b border-[var(--p-hairline)] px-4 pb-16 pt-14 text-center">
          <Mark height={52} assemble={!assembled} />
          <h1 className="mt-10 font-serif text-[clamp(40px,6vw,64px)] font-light leading-[1.06] tracking-[-0.01em] text-[var(--p-ink)]">
            Design Language
          </h1>
          <div className="mt-8 h-px w-9 bg-[var(--p-gold)]" />
          <p className="mt-7 max-w-[52ch] font-serif text-[clamp(17px,2vw,21px)] font-light italic leading-relaxed text-[var(--p-ink-soft)]">
            One system, one voice — <span className="goldi">decisions that hold</span>, on paper and on screen.
          </p>
          <p className="eyebrow mt-9">Peak Priorities &nbsp;·&nbsp; Design System &nbsp;·&nbsp; V3</p>
        </header>

        {/* ── 01 The voice ── */}
        <section id="voice" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="01" eyebrow="The voice" sub="Two grounds, one language. Daylight is paper, ink, and a single gold phrase. Night is the diagram canvas. Everything else is shared.">
            Ink for the argument, <span className="goldi">gold for what matters most.</span>
          </SectionHead>

          {/* The four fonts, each speaking in its own voice */}
          <div className="border border-[var(--p-hairline)]">
            <BarRow bar="var(--p-ink)" term="Spectral" def="The display voice. Headlines in roman ink; the one phrase that matters, in gold italic." />
            <BarRow bar="var(--p-label)" term="Degular" def="The working voice. Body, descriptions, interface copy — quiet and legible." />
            <BarRow bar="var(--p-label)" term="Fieldwork" def="The structural voice. Section titles and navigation inside the product." />
            <BarRow bar="var(--p-gold)" term="JetBrains Mono" def="The instrument voice. Data, codes, timestamps — evidence, set in tabular figures." />
          </div>

          {/* The palette with its laws */}
          <div className="mt-10 grid grid-cols-2 gap-px border border-[var(--p-hairline)] bg-[var(--p-hairline)] sm:grid-cols-5">
            {PALETTE.map((c) => (
              <div key={c.name} className="bg-[var(--p-paper)] p-5">
                <div className="h-10 w-full" style={{ background: `var(${c.varName})` }} />
                <div className="mt-3 font-serif text-[15px] text-[var(--p-ink)]">{c.name}</div>
                <div className="mt-1 font-body text-[13px] leading-snug text-[var(--p-ink-soft)]">{c.law}</div>
              </div>
            ))}
          </div>

          {/* Which palette is for what. Two palettes, each with one job. The older colours are
              deprecated and sit on the Legacy design pages page, in the Archive. */}
          <h3 className="mt-12 font-serif text-[21px] font-normal text-[var(--p-ink)]">Two palettes, each with one job</h3>
          <div className="mt-4 border border-[var(--p-hairline)]">
            <BarRow bar="var(--p-ink)" term="Paper" def="Light mode, everywhere: the public site, the app, and diagrams when shown in light. The site's stylesheet is the source; the app follows it. Values on the Token system page, Light column." />
            <BarRow bar="var(--p-label)" term="Night" def="Dark mode: the app, the diagram canvas, the exhibition. Ground #0B0F19; gold #D4A017, clay #C0533A, forest #3A7D56, slate #3D4B6F. Values on the Token system page, Dark column." />
          </div>
          <p className="mt-4 max-w-[62ch] font-body text-[15px] leading-relaxed text-[var(--p-ink-soft)]">
            Two more colours belong to diagrams only: brown for what already exists, fog gray for examples.
          </p>

          {/* The laws — cream callout, gold rule, serif italic. The deck's callout, verbatim as a motif. */
          }
          <div className="mt-10 border-l-[3px] border-[var(--p-gold)] bg-[var(--p-cream)] px-8 py-7">
            <p className="font-serif text-[17px] font-light italic leading-[2.05] text-[var(--p-ink)]">
              Four fonts, four jobs — never interchangeable.<br />
              Color is semantic, never decorative — and <span className="goldi">gold is spent on one phrase at a time.</span><br />
              Depth is a glow or a plate, never a shadow. Separation is a hairline, never a box.<br />
              Empty space is intentional. Geometry is square. The mark is never redrawn.
            </p>
          </div>
        </section>

        {/* ── 02 Foundations ── */}
        <section id="foundations" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="02" eyebrow="Foundations" sub="The working references — every token, color, and type decision, verified line-by-line against the source.">
            The source of truth, <span className="goldi">kept honest.</span>
          </SectionHead>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
            <Card featured wide to="/elevation/priorities-ai-design-language/token-system" eye="Canonical" title="Token system"
              desc="Every surface, accent, text, and hover token in both modes — the two theming mechanisms, the hover vocabulary, per-surface ramps. Verified against index.css and held by a ratchet that only tightens." />
            <Card to="/elevation/priorities-ai-design-language/colors" eye="Foundation" title="Colors"
              desc="The dual-mode palette, semantic accents, and how each earns its place." />
            <Card to="/elevation/priorities-ai-design-language/typography" eye="Foundation" title="Typography"
              desc="The four-font system — roles, scale, and the rules that keep them apart." />
            <Card to="/elevation/priorities-ai-design-language/foundations" eye="Rules" title="Foundations"
              desc="Four rules that are easy to break silently — each fails only in one theme, so lint and build never see it." />
            <Card to="/elevation/priorities-ai-design-language/layout-grid-system" eye="Foundation" title="Layout & grid"
              desc="A 4px base scale and a page-width convention, with counts measured from the code." />
            <Card to="/elevation/priorities-ai-design-language/icon-library" eye="Foundation" title="Icon library"
              desc="Lucide is the icon set. One glyph per meaning — which one means what." />
            <Card to="/elevation/priorities-ai-design-language/infographic-data-viz-standards" eye="Foundation" title="Data visualization"
              desc="One charting library and one theme, so charts follow light and dark." />
          </div>
        </section>

        {/* ── 03 Components ── */}
        <section id="components" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="03" eyebrow="Components" sub="Not documentation of components — the components, rendered from source, in both modes.">
            The real thing, <span className="goldi">rendered live.</span>
          </SectionHead>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card featured wide to="/elevation/design-system/components" eye="Live catalog" title="Component catalog"
              desc="Atoms → molecules → organisms with a light / dark toggle. Provider-bound components documented; missing primitives flagged as gaps, on the record." />
          </div>
        </section>

        {/* ── 04 Brand ── */}
        <section id="brand" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="04" eyebrow="Brand" sub="The mark, the identity, and how they travel — from this page to a favicon to a deck.">
            Six bars, drawn once, <span className="goldi">never redrawn.</span>
          </SectionHead>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
            <Card to="/elevation/priorities-ai-design-language/logo-system" eye="Identity" title="Logo system"
              desc="Mark and wordmark on light and dark plates; clear space and minimum size." />
            <Card to="/elevation/priorities-ai-design-language/brand-identity" eye="Identity" title="Brand identity"
              desc="Canonical brand color and how the identity enters the product." />
            <Card to="/elevation/priorities-ai-design-language/app-icons" eye="Identity" title="Favicon & app icons"
              desc="The mark at 16 through 512 pixels — sizes, formats, legibility." />
          </div>
        </section>

        {/* ── 05 Visual language ── */}
        <section id="visual" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="05" eyebrow="Visual language" sub="The night voice: the grammar of the diagrams, on the dark canvas, where the shape is the argument. The engine that draws them is in the Exhibition, below.">
            Twelve moves, <span className="goldi">one grammar.</span>
          </SectionHead>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
            <Card external to="/visual-language/field-guide.html" eye="Reference" title="Field guide"
              desc="The 12 conceptual moves, palette, grammar, and badges." />
            <Card external to="/visual-language/kit.html" eye="Reference" title="Visual language kit"
              desc="Typography, badges, callouts, and shell patterns — copy and travel." />
            <Card external to="/visual-language/grammar-spec.html" eye="Reference" title="Grammar specification"
              desc="Primitives, color semantics, anti-patterns, the contract." />
          </div>
        </section>

        {/* ── 06 Exhibition ── */}
        <section id="exhibition" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="06" eyebrow="Exhibition" sub="The language, playable. The diagram engine, eighty-six working pieces on the night canvas, every diagram drawn for the site, and the same ideas by hand.">
            The argument, <span className="goldi">in your hands.</span>
          </SectionHead>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card featured to="/visual-language" eye="Engine" title="Diagram engine"
              desc="All concept diagrams, wayfinding terrain, and templates — rendered live." />
            <Card featured external to={`${import.meta.env.BASE_URL}site-configurator/`} eye="Builder" title="Site builder"
              desc="Compose site pages and slides from sections, and make new sections and diagrams in the brand's style." />
            <Card featured external to="/visual-language/prototypes/index.html" eye="Wing I · 36 pieces" title="Prototype Exhibition"
              desc="Thirty-six working arguments about choosing — the cost of yes, the shape of no, the weight of a commitment. Each one an idea made physical." />
            <Card featured external to="/visual-language/lagniappes/index.html" eye="Wing II · 50 pieces" title="Lagniappes"
              desc="Fifty small gifts — living marks, states of grace, typographic jewelry, quiet rituals. What a brand does when it thinks no one is watching." />
            <Card featured external to="/visual-language/site-figures/index.html" eye="Wing III · the site's diagrams" title="Site figures"
              desc="Every diagram drawn for the public site, shown alone on paper, with where it is used. New figures are made here before a page uses them." />
            <Card featured external to="/visual-language/hand-drawn/index.html" eye="Wing IV · sketches" title="Hand drawn"
              desc="The same ideas, drawn here in a loose, hand-drawn line — the way you would sketch them for someone across the table. Five to start." />
          </div>
        </section>

        {/* ── 07 Agent skills & records ── */}
        <section id="skills" className="scroll-mt-6 border-b border-[var(--p-hairline)] py-14">
          <SectionHead num="07" eyebrow="Skills & records" sub="The system as agents read it, and the record of what changed. Each skill is a file in the repository; the link opens it here, as written.">
            Written down, so it <span className="goldi">survives its authors.</span>
          </SectionHead>

          <div className="border border-[var(--p-hairline)]">
            {DESIGN_SKILLS.filter((k) => !k.deprecated).map((k, i) => (
              <Link
                key={k.name}
                to={`${SKILL_ROUTE_BASE}/${k.name}`}
                className="dl3-card group flex items-stretch gap-0 border-b border-[var(--p-hairline)] last:border-b-0"
              >
                <span className="w-[3px] shrink-0 self-stretch" style={{ background: SKILL_BARS[i % SKILL_BARS.length] }} />
                <div className="flex flex-1 flex-col gap-1 py-4 pl-6 pr-4 sm:flex-row sm:items-baseline sm:gap-8">
                  <span className="w-64 shrink-0 font-mono text-[14px] text-[var(--p-ink)]">{k.name}</span>
                  <span className="flex-1 font-body text-[14.5px] leading-relaxed text-[var(--p-ink-soft)]">{k.def}</span>
                  <span className="go font-body text-[13px] text-[var(--p-gold)]">Open →</span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-2.5">
            <Chip external to="/visual-language/delta.html" label="The Delta — what changed" />
            <Chip external to="/visual-language/elevation-map.html" label="Site map & conflicts" />
          </div>
        </section>

        {/* ── Legacy ── everything deprecated lives on its own page, linked from the Archive (owner, 2026-10-02) */}
        <p className="py-10 font-body text-[14.5px] leading-relaxed text-[var(--p-ink-soft)]">
          Older pages, colors, and the earlier logo are deprecated. They are kept on one page,{' '}
          <Link to="/elevation/archive/design-language-legacy" className="text-[var(--p-gold)] underline underline-offset-4">
            Legacy design pages
          </Link>
          , in the{' '}
          <Link to="/elevation/archive" className="text-[var(--p-gold)] underline underline-offset-4">Archive</Link>.
        </p>

        {/* ── footer — the deck's closing furniture ── */}
        <footer className="flex items-center justify-between border-t border-[var(--p-hairline)] py-6">
          <span className="eyebrow text-[13px]">Peak Priorities LLC &nbsp;|&nbsp; priorities.ai</span>
          <Mark height={16} />
          <span className="eyebrow text-[13px]">Design Language · V3 · 2026</span>
        </footer>
      </div>
    </div>
  );
};
